<!-- Statistics Cards -->
<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
	@for (stat of stats(); track stat.label) {
		<div class="stat-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-charcoal/5 hover:border-primary/20 relative overflow-hidden">
			<!-- Background Gradient -->
			<div class="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-5 transition-opacity duration-500"
				[style.background]="'linear-gradient(135deg, ' + stat.color + ', transparent)'"></div>

			<div class="relative z-10">
				<div class="flex items-start justify-between mb-4">
					<div class="w-14 h-14 rounded-xl flex items-center justify-center shadow-lg transition-all duration-500 group-hover:scale-110"
						[style.background]="'linear-gradient(135deg, ' + stat.color + ', ' + stat.colorDark + ')'">
						<svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="stat.icon"/>
						</svg>
					</div>
					@if (stat.change()) {
						<div class="flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold"
							[ngClass]="stat.trend() === 'up' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'">
							<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path *ngIf="stat.trend() === 'up'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
								<path *ngIf="stat.trend() === 'down'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
							</svg>
							{{ stat.change() }}
						</div>
					}
				</div>
				<h3 class="text-charcoal/60 text-sm font-medium mb-2">{{ stat.label }}</h3>
				<p class="text-3xl font-cenzo font-bold text-primary mb-1">{{ stat.value() }}</p>
				<p class="text-xs text-charcoal/50">vs last month</p>
			</div>
		</div>
	}
</div>

<!-- Charts Section -->
<div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
	<!-- Appointments Trend Chart -->
	<div class="bg-white rounded-2xl p-6 shadow-lg border border-charcoal/5">
		<div class="flex items-center justify-between mb-6">
			<div>
				<h3 class="text-lg font-cenzo font-bold text-primary">Appointments Trend</h3>
				<p class="text-sm text-charcoal/60 mt-1">Track appointment patterns</p>
			</div>
			<div class="flex gap-2 flex-wrap">
				<button
					(click)="onPeriodChange('today')"
					[class.bg-primary]="selectedPeriod() === 'today'"
					[class.text-white]="selectedPeriod() === 'today'"
					[class.bg-mist]="selectedPeriod() !== 'today'"
					[class.text-charcoal]="selectedPeriod() !== 'today'"
					class="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-300 hover:scale-105">
					Today
				</button>
				<button
					(click)="onPeriodChange('2days')"
					[class.bg-primary]="selectedPeriod() === '2days'"
					[class.text-white]="selectedPeriod() === '2days'"
					[class.bg-mist]="selectedPeriod() !== '2days'"
					[class.text-charcoal]="selectedPeriod() !== '2days'"
					class="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-300 hover:scale-105">
					2 Days
				</button>
				<button
					(click)="onPeriodChange('7days')"
					[class.bg-primary]="selectedPeriod() === '7days'"
					[class.text-white]="selectedPeriod() === '7days'"
					[class.bg-mist]="selectedPeriod() !== '7days'"
					[class.text-charcoal]="selectedPeriod() !== '7days'"
					class="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-300 hover:scale-105">
					7 Days
				</button>
				<button
					(click)="onPeriodChange('month')"
					[class.bg-primary]="selectedPeriod() === 'month'"
					[class.text-white]="selectedPeriod() === 'month'"
					[class.bg-mist]="selectedPeriod() !== 'month'"
					[class.text-charcoal]="selectedPeriod() !== 'month'"
					class="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-300 hover:scale-105">
					Month
				</button>
				<button
					(click)="onPeriodChange('6months')"
					[class.bg-primary]="selectedPeriod() === '6months'"
					[class.text-white]="selectedPeriod() === '6months'"
					[class.bg-mist]="selectedPeriod() !== '6months'"
					[class.text-charcoal]="selectedPeriod() !== '6months'"
					class="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-300 hover:scale-105">
					6 Months
				</button>
			</div>
		</div>

		<!-- Chart.js Bar Chart -->
		<div class="space-y-4">
			<!-- Chart Canvas -->
			<div class="h-80 p-4 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl">
				<canvas #trendChart></canvas>
			</div>

			<!-- Summary Stats -->
			@if (trendChartData().length > 0) {
				<div class="grid grid-cols-3 gap-3 pt-4 border-t border-charcoal/10">
					<div class="text-center">
						<p class="text-xs text-charcoal/60 mb-1">Highest</p>
						<p class="text-lg font-bold text-primary">{{ getMaxChartValue() }}</p>
					</div>
					<div class="text-center">
						<p class="text-xs text-charcoal/60 mb-1">Average</p>
						<p class="text-lg font-bold text-primary">{{ getTrendAverage() }}</p>
					</div>
					<div class="text-center">
						<p class="text-xs text-charcoal/60 mb-1">Total</p>
						<p class="text-lg font-bold text-primary">{{ getTrendTotal() }}</p>
					</div>
				</div>
			}
		</div>
	</div>

	<!-- Appointments Status Distribution -->
	<div class="bg-white rounded-2xl p-6 shadow-lg border border-charcoal/5">
		<div class="mb-6">
			<h3 class="text-lg font-cenzo font-bold text-primary">Booking Status</h3>
			<p class="text-sm text-charcoal/60 mt-1">Current distribution</p>
		</div>
		
		@if (statusChartData().length > 0) {
			<div class="flex flex-col items-center space-y-6">
				<!-- Chart.js Donut Chart -->
				<div class="relative w-full h-64">
					<canvas #statusChart></canvas>
					<!-- Center Text Overlay -->
					<div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
						<p class="text-4xl font-bold text-primary">{{ getTotalAppointments() }}</p>
						<p class="text-xs text-charcoal/60 mt-1">Total</p>
					</div>
				</div>

				<!-- Legend -->
				<div class="w-full space-y-3">
					@for (item of statusChartData(); track item.label) {
						<div class="flex items-center justify-between">
							<div class="flex items-center gap-2">
								<div class="w-4 h-4 rounded-full" [style.background]="item.color"></div>
								<span class="text-sm font-medium text-charcoal">{{ item.label }}</span>
							</div>
							<div class="flex items-center gap-3">
								<span class="text-sm text-charcoal/60">{{ item.value }}</span>
								<span class="text-sm font-semibold text-primary min-w-[45px] text-right">{{ item.percentage }}%</span>
							</div>
						</div>
					}
				</div>
			</div>
		} @else {
			<div class="h-64 flex items-center justify-center">
				<div class="text-center">
					<svg class="w-16 h-16 text-charcoal/20 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"/>
					</svg>
					<p class="text-charcoal/40 text-sm">No appointment data</p>
				</div>
			</div>
		}
	</div>
</div>

<!-- Recent Activity & Quick Actions -->
<div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
	<!-- Recent Appointments -->
	<div class="lg:col-span-2 bg-white rounded-2xl p-6 shadow-lg border border-charcoal/5">
		<div class="flex items-center justify-between mb-6">
			<div>
				<h3 class="text-lg font-cenzo font-bold text-primary">Recent Appointments</h3>
				<p class="text-sm text-charcoal/60 mt-1">Latest bookings from customers</p>
			</div>
			<a href="#" class="text-sm text-primary hover:text-accent font-semibold transition-colors">View All →</a>
		</div>
		<div class="space-y-3">
			@if (isLoading()) {
				<!-- Loading State -->
				@for (i of [1,2,3,4,5]; track i) {
					<div class="flex items-center gap-4 p-4 rounded-xl bg-mist/50 animate-pulse">
						<div class="w-12 h-12 rounded-full bg-charcoal/10"></div>
						<div class="flex-1 space-y-2">
							<div class="h-4 bg-charcoal/10 rounded w-32"></div>
							<div class="h-3 bg-charcoal/10 rounded w-48"></div>
						</div>
						<div class="w-20 h-4 bg-charcoal/10 rounded"></div>
					</div>
				}
			} @else if (recentAppointments().length > 0) {
				@for (appointment of recentAppointments(); track appointment.id) {
					<div class="flex items-center gap-4 p-4 rounded-xl bg-mist/50 hover:bg-mist transition-colors group">
						<div class="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
							<span class="text-white font-bold text-sm">{{ appointment.initials }}</span>
						</div>
						<div class="flex-1 min-w-0">
							<p class="font-semibold text-charcoal truncate">{{ appointment.name }}</p>
							<p class="text-sm text-charcoal/60">{{ appointment.service }}</p>
						</div>
						<div class="text-right">
							<p class="text-sm font-medium text-charcoal">{{ appointment.date }}</p>
							<p class="text-xs text-charcoal/60">{{ appointment.time }}</p>
						</div>
						<div class="px-3 py-1 rounded-full text-xs font-semibold"
							[ngClass]="{
								'bg-green-100 text-green-700': appointment.status === 'confirmed',
								'bg-yellow-100 text-yellow-700': appointment.status === 'pending',
								'bg-blue-100 text-blue-700': appointment.status === 'completed',
								'bg-red-100 text-red-700': appointment.status === 'cancelled'
							}">
							{{ appointment.status }}
						</div>
					</div>
				}
			} @else {
				<!-- Empty State -->
				<div class="text-center py-8">
					<svg class="w-16 h-16 text-charcoal/20 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
					</svg>
					<p class="text-charcoal/40 text-sm">No recent appointments</p>
				</div>
			}
		</div>
	</div>

	<!-- Quick Actions -->
	<div class="bg-gradient-to-br from-primary to-charcoal rounded-2xl p-6 shadow-lg text-white">
		<h3 class="text-lg font-cenzo font-bold mb-6">Quick Actions</h3>
		<div class="space-y-3">
			<button 
				(click)="navigateToAddService()"
				class="w-full flex items-center gap-3 p-4 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-300 group cursor-pointer">
				<div class="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
					<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
					</svg>
				</div>
				<span class="font-medium">New Service</span>
			</button>
			<button 
				(click)="navigateToAppointments()"
				class="w-full flex items-center gap-3 p-4 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-300 group cursor-pointer">
				<div class="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
					<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
					</svg>
				</div>
				<span class="font-medium">View Appointments</span>
			</button>
			<button 
				(click)="navigateToReports()"
				class="w-full flex items-center gap-3 p-4 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-300 group cursor-pointer">
				<div class="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
					<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
					</svg>
				</div>
				<span class="font-medium">Reports</span>
			</button>
			<button 
				(click)="navigateToSettings()"
				class="w-full flex items-center gap-3 p-4 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-300 group cursor-pointer">
				<div class="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
					<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
					</svg>
				</div>
				<span class="font-medium">Settings</span>
			</button>
		</div>
	</div>
</div>
